import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  LayoutDashboard,
  Package,
  Calendar,
  Users,
  DollarSign,
  MessageSquare,
  Boxes,
  TrendingUp,
  ArrowRight,
  Eye,
  Loader2,
  FileSpreadsheet,
  Menu
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user, token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, ordersRes, rentalsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders?limit=5`, { headers }),
        axios.get(`${API}/admin/rentals?limit=5`, { headers }),
        axios.get(`${API}/admin/messages?unread_only=true`, { headers })
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setRentals(rentalsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA');
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-gray-100 text-gray-800 border-gray-200',
      processing: 'bg-gray-200 text-gray-800 border-gray-300',
      shipped: 'bg-gray-100 text-gray-700 border-gray-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      returned: 'bg-gray-100 text-gray-600 border-gray-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return <Badge className={`${colors[status] || 'bg-gray-100'} border`}>{status}</Badge>;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin()) return null;

  const statCards = [
    { 
      title: 'Commandes', 
      value: stats?.orders?.total || 0, 
      pending: stats?.orders?.pending || 0,
      icon: Package
    },
    { 
      title: 'Locations', 
      value: stats?.rentals?.total || 0, 
      active: stats?.rentals?.active || 0,
      icon: Calendar
    },
    { 
      title: 'Licences', 
      value: stats?.licenses?.total || 0, 
      active: stats?.licenses?.active || 0,
      icon: Boxes
    },
    { 
      title: 'Clients', 
      value: stats?.users?.total || 0, 
      icon: Users
    },
  ];

  return (
    <div className="min-h-screen py-8 bg-gray-50" data-testid="admin-dashboard">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <LayoutDashboard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-black">Tableau de bord</h1>
                <p className="text-gray-500">Bienvenue, {user?.name}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/admin/menu">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="admin-menu-link">
                  <Menu className="h-4 w-4 mr-2" />
                  Menu
                </Button>
              </Link>
              <Link to="/admin/quote-requests">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="admin-quote-requests-link">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Soumissions
                </Button>
              </Link>
              <Link to="/admin/products">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                  <Boxes className="h-4 w-4 mr-2" />
                  Produits
                </Button>
              </Link>
              <Link to="/admin/finance">
                <Button className="bg-black text-white hover:bg-gray-800">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Finances
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-32 bg-gray-100" />
              ))
            ) : (
              statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card className="bg-white border-gray-200">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-black" />
                          </div>
                          {stat.pending !== undefined && stat.pending > 0 && (
                            <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">
                              {stat.pending} en attente
                            </Badge>
                          )}
                          {stat.active !== undefined && (
                            <Badge className="bg-green-100 text-green-800 border border-green-200">
                              {stat.active} actifs
                            </Badge>
                          )}
                        </div>
                        <p className="text-3xl font-bold text-black font-mono">{stat.value}</p>
                        <p className="text-gray-500 text-sm">{stat.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Revenue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-gray-500">Revenus ce mois</span>
                  </div>
                </div>
                <p className="text-3xl font-bold text-black font-mono">
                  {formatPrice(stats?.revenue?.monthly || 0)}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-gray-500">Revenus annuels</span>
                  </div>
                </div>
                <p className="text-3xl font-bold text-black font-mono">
                  {formatPrice(stats?.revenue?.yearly || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Orders */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-black flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Commandes récentes
                </CardTitle>
                <Link to="/admin/orders">
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-black">
                    Voir tout
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-16 bg-gray-100" />
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucune commande récente</p>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-black text-sm">#{order.id.slice(0, 8)}</p>
                          <p className="text-gray-500 text-xs">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-black">{formatPrice(order.total)}</p>
                          {getStatusBadge(order.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Rentals */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-black flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Locations récentes
                </CardTitle>
                <Link to="/admin/rentals">
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-black">
                    Voir tout
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-16 bg-gray-100" />
                    ))}
                  </div>
                ) : rentals.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucune location récente</p>
                ) : (
                  <div className="space-y-3">
                    {rentals.map((rental) => (
                      <div key={rental.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-black text-sm">{rental.product_name}</p>
                          <p className="text-gray-500 text-xs">#{rental.id.slice(0, 8)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-black">{formatPrice(rental.total)}</p>
                          {getStatusBadge(rental.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Unread Messages */}
          {messages.length > 0 && (
            <Card className="bg-white border-gray-200 mt-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-black flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Messages non lus ({messages.length})
                </CardTitle>
                <Link to="/admin/messages">
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-black">
                    Voir tout
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {messages.slice(0, 3).map((message) => (
                    <div key={message.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-black text-sm">{message.name}</p>
                        <p className="text-gray-500 text-xs">{message.subject}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="text-gray-500 hover:text-black">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
    </div>
  );
}
